import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  AppBar,
  Badge,
  Box,
  Button,
  IconButton,
  Toolbar,
  Typography
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';

import {
  AddOutlined,
  CalendarMonthOutlined,
  GridViewOutlined,
  HomeOutlined,
  MailOutlineOutlined,
  MessageOutlined,
  SettingsOutlined
} from '@mui/icons-material';

import PatientAddDialog from './PatientAddDialog';
import UserProfileMenu from './UserProfileMenu';

import PatientAutocomplete from '../../../../views/Common/components/Patients/PatientAutocomplete';

import logo from '../../../../assets/images/logo_new.png';
import { topbarStyles } from './TopBarStyles';
import { HomeIcon } from '../../../../views/Common/components/Icons/icons';

const patients = [
  {
    dob: '03/06/2006 (20 yrs)',
    mr: '22351612',
    name: 'William, Gwen Marry',
    sex: 'Female'
  },
  {
    dob: '05/12/1998 (28 yrs)',
    mr: '22351684',
    name: 'Philip, Cammiee John',
    sex: 'Female'
  },
  {
    dob: '11/22/1985 (40 yrs)',
    mr: '22351690',
    name: 'Johnson, Arjun Kumar',
    sex: 'Male'
  },
  {
    dob: '08/14/1992 (33 yrs)',
    mr: '22351701',
    name: 'Mary, Anitha Rose',
    sex: 'Female'
  },
  {
    dob: '01/19/1978 (48 yrs)',
    mr: '22351718',
    name: 'Kumar, Ravi Shankar',
    sex: 'Male'
  }
];

const Topbar = ({ onSidebarOpen }) => {
  const location = useLocation();

  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [openPatientAdd, setOpenPatientAdd] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formattedDate = currentDateTime
    .toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
    .toUpperCase();

  const formattedTime = currentDateTime.toLocaleTimeString('en-US', {
    hour: '2-digit',
    hour12: true,
    minute: '2-digit'
  });

  const handleSelectPatient = patient => {
    console.log('Selected patient:', patient);

    // Add patient navigation here.
    // Example:
    // navigate(`/soap/${patient.mr}`);
  };

  const menuItems = [
    {
      icon: <HomeIcon />,
      iconSize: 20,
      label: 'Home',
      path: '/default'
    },
    {
      dot: true,
      icon: <MessageOutlined />,
      label: 'Communications',
      path: '/communications'
    },
    {
      icon: <CalendarMonthOutlined />,
      label: 'Schedule',
      path: '#',
      disabled: true
    },
    {
      dot: true,
      icon: <MailOutlineOutlined />,
      label: 'PT Messages',
      path: '/pt-message'
    },
    {
      icon: <GridViewOutlined />,
      label: 'Templates',
      disabled: true
    },
    {
      icon: <SettingsOutlined />,
      label: 'Settings',
      path: '/settings'
    }
  ];

  return (
    <>
      <AppBar position="static" sx={topbarStyles.appBar}>
        <Toolbar sx={topbarStyles.toolbar}>
          {/* <RouterLink style={{ textDecoration: 'none' }} to="/default">
            <Box alt="Logo" component="img" src={logo} sx={topbarStyles.logo} />
          </RouterLink> */}

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flex: 1,
              gap: 1.5,
              justifyContent: 'flex-start'
            }}>
            <RouterLink style={{ textDecoration: 'none' }} to="/default">
              <Box
                alt="Logo"
                component="img"
                src={logo}
                sx={topbarStyles.logo}
              />
            </RouterLink>
            <Button
              onClick={() => setOpenPatientAdd(true)}
              size="small"
              startIcon={
                <AddOutlined
                  sx={{
                    color: '#424242',
                    fontSize: 18
                  }}
                />
              }
              sx={topbarStyles.addPatientButton}
              variant="outlined">
              Add Patient
            </Button>

            <PatientAutocomplete
              onSelectPatient={handleSelectPatient}
              patients={patients}
              width="50%"
              placeholder="Search by patient name or MR#"
            />
          </Box>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flex: 1,
              gap: 1,
              justifyContent: 'flex-end'
            }}>
            <Box sx={topbarStyles.menuWrapper}>
              {menuItems.map(item => {
                const isActive =
                  item.path !== '#' && location.pathname === item.path;

                const menuContent = (
                  <Box
                    sx={{
                      ...topbarStyles.menuItem(isActive),
                      cursor: item.disabled ? 'default' : 'pointer',
                      opacity: item.disabled ? 1 : 1
                    }}>
                    <Box sx={topbarStyles.menuIconBox}>
                      <Badge
                        anchorOrigin={{
                          horizontal: 'left',
                          vertical: 'top'
                        }}
                        color="error"
                        invisible={!item.dot}
                        overlap="circular"
                        sx={{
                          '& .MuiBadge-badge': {
                            borderRadius: '50%',
                            height: 5,
                            left: -4,
                            minWidth: 5,
                            top: 2,
                            width: 5
                          }
                        }}
                        variant="dot">
                        {React.cloneElement(item.icon, {
                          sx: {
                            fontSize: item.iconSize || 18
                          }
                        })}
                      </Badge>
                    </Box>

                    {item.label}
                  </Box>
                );

                if (item.disabled) {
                  return (
                    <Box
                      key={item.label}
                      sx={{
                        cursor: 'default',
                        pointerEvents: 'none'
                      }}>
                      {menuContent}
                    </Box>
                  );
                }

                return (
                  <RouterLink
                    key={item.label}
                    style={{ textDecoration: 'none' }}
                    to={item.path}>
                    {menuContent}
                  </RouterLink>
                );
              })}
            </Box>

            <Box
              sx={{
                ...topbarStyles.dateBox,
                display: {
                  xs: 'none',
                  sm: 'none',
                  md: 'none',
                  lg: 'block'
                }
              }}>
              <Typography sx={topbarStyles.dateText}>
                {formattedDate}
              </Typography>

              <Typography sx={topbarStyles.timeText}>
                {formattedTime}
              </Typography>
            </Box>

            <Box sx={topbarStyles.mobileMenu}>
              <IconButton
                aria-label="Open navigation menu"
                onClick={onSidebarOpen}>
                <MenuIcon />
              </IconButton>
            </Box>
            <UserProfileMenu
              onLogout={() => {
                localStorage.clear();
                window.location.href = '/';
              }}
            />
          </Box>
        </Toolbar>
      </AppBar>

      <PatientAddDialog
        onClose={() => setOpenPatientAdd(false)}
        open={openPatientAdd}
      />
    </>
  );
};

Topbar.propTypes = {
  onSidebarOpen: PropTypes.func
};

Topbar.defaultProps = {
  onSidebarOpen: () => {}
};

export default Topbar;
